/**
 * OperationInspectorKpiCard — inspector for kpi_card nodes (ADR-0070 §3.2b).
 *
 * ADR-0129 — on the shared spine: Info (node label) · Setup (KPI items editor)
 * · Data (live KPI render) · AI (viz copilot, frame-filling chat). The
 * persistent Run / last-run toolbar sits above the tab rack.
 *
 * Chat panel posts to the operation_viz session endpoint, which the hub
 * routes to `runKpiOrchestrator` based on the node's type (ADR-0070 §4.6.2,
 * wired in Phase 5.5). The returned `meta.persistableKpiSpec` is auto-applied
 * to `config.spec_kpi_card` (no Apply button — spec §3.2b).
 *
 * Run executes the node end-to-end and pulls the cached render_out for the
 * KPI preview pane.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aiRouteErrorCode } from "../../lib/aiRouteError.js";
import type { Node } from "@xyflow/react";
import type { OperationCanvasNodeData } from "../OperationCanvasNode.js";
import { apiFetch, ApiRequestError } from "../../lib/api.js";
import { useToast } from "../Toast.js";
import { useConfirm } from "../ConfirmDialog.js";
import { useAiChatThread, type AiChatMessage } from "../../hooks/useAiChatThread.js";
import { useOperationRun } from "../../hooks/useOperationRun.js";
import {
  ChatMessageList,
  ChatComposer,
  ChatThinkingIndicator,
  ChatErrorBanner
} from "../aiChat/index.js";
import { KpiCard, type KpiMetric, type KpiStyle } from "../KpiCard.js";
import {
  emptyKpiCardSpec,
  validateKpiCardSpec,
  KPI_AGGREGATIONS,
  KPI_FORMAT_KINDS,
  type KpiCardItem,
  type KpiCardVizSpecV1,
} from "../../lib/kpiCardSpec.js";
import { InspectorTabs } from "../inspector/InspectorTabs.js";

const NUMERIC_TYPES = new Set(["number", "integer", "int", "float", "double", "decimal", "numeric", "currency", "percent"]);

const INSPECTOR_WIDTH_STYLE: React.CSSProperties = { width: "100%", maxWidth: "100%", minWidth: 0 };

type ChatMessage = AiChatMessage;
type InteractionMode = "agent" | "ask";

interface Props {
  selectedNode: Node<OperationCanvasNodeData>;
  operationId: string;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  flushAndSave: () => Promise<boolean>;
  tenantAiModels?: Array<{ model_id: string; status: string }>;
  selectedAiModelId?: string;
  onTenantAiModelChange?: (modelId: string) => void;
  setRunStatus?: (nodeIds: string[], status: "running" | "success" | "error" | "idle") => void;
}

type RenderModelKpi = {
  kind: "kpi";
  metrics: KpiMetric[];
  style: KpiStyle;
  detailData?: { columns: string[]; rows: unknown[][] };
};

const sessionLastRunByNodeId = new Map<string, { lastRunAt: string; lastRowCount: number | null }>();

export function OperationInspectorKpiCard({
  selectedNode,
  operationId,
  setNodeData,
  flushAndSave,
  tenantAiModels = [],
  selectedAiModelId,
  onTenantAiModelChange,
  setRunStatus,
}: Props) {
  const toast = useToast();
  const confirm = useConfirm();
  const runController = useOperationRun();
  const nodeId = selectedNode.id;

  // ─── current spec (read-through) ───
  const config = (selectedNode.data.config ?? {}) as Record<string, unknown>;
  const rawSpec = config.spec_kpi_card;
  const currentSpec: KpiCardVizSpecV1 = useMemo(() => {
    if (!rawSpec || typeof rawSpec !== "object") return emptyKpiCardSpec();
    const v = validateKpiCardSpec(rawSpec);
    return v.ok ? v.spec : emptyKpiCardSpec();
  }, [rawSpec]);

  // ─── interaction mode ───
  const [interactionMode, setInteractionMode] = useState<InteractionMode>("agent");

  // ─── upstream schema (for column dropdown filtering) ───
  const [upstreamColumns, setUpstreamColumns] = useState<Array<{ name: string; type: string }>>([]);
  const [upstreamLoading, setUpstreamLoading] = useState(false);
  const [upstreamError, setUpstreamError] = useState<string | null>(null);

  const loadUpstreamColumns = useCallback(async () => {
    setUpstreamLoading(true);
    setUpstreamError(null);
    try {
      const data = await apiFetch<{ transformResult?: { columns?: Array<{ name: string; type: string }> } }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/cache?port_id=tabular_in`
      );
      const cols = data.transformResult?.columns ?? [];
      setUpstreamColumns(cols.filter((c) => typeof c.name === "string" && c.name.length > 0));
    } catch (e) {
      if (e instanceof ApiRequestError && e.status === 404) {
        setUpstreamColumns([]);
      } else {
        setUpstreamError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setUpstreamLoading(false);
    }
  }, [operationId, nodeId]);

  useEffect(() => { void loadUpstreamColumns(); }, [loadUpstreamColumns]);

  // ─── render preview ───
  const [renderModel, setRenderModel] = useState<RenderModelKpi | null>(null);
  const [renderLoading, setRenderLoading] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);

  const loadRenderModel = useCallback(async () => {
    setRenderLoading(true);
    setRenderError(null);
    try {
      const data = await apiFetch<{ renderModel?: unknown }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/cache?port_id=render_out`
      );
      const rm = data.renderModel as Record<string, unknown> | undefined;
      if (rm && rm.kind === "kpi" && Array.isArray(rm.metrics)) {
        setRenderModel(rm as unknown as RenderModelKpi);
      } else {
        setRenderModel(null);
      }
    } catch (e) {
      if (e instanceof ApiRequestError && e.status === 404) {
        setRenderModel(null);
      } else {
        setRenderError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setRenderLoading(false);
    }
  }, [operationId, nodeId]);

  useEffect(() => { void loadRenderModel(); }, [loadRenderModel]);

  // ─── run preview ───
  const [runBusy, setRunBusy] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const lastRunStored = sessionLastRunByNodeId.get(nodeId) ?? null;
  const [lastRunAt, setLastRunAt] = useState<string | null>(lastRunStored?.lastRunAt ?? null);
  const [lastRowCount, setLastRowCount] = useState<number | null>(lastRunStored?.lastRowCount ?? null);

  const runNode = useCallback(async () => {
    setRunBusy(true);
    setRunError(null);
    setRunStatus?.([nodeId], "running");
    try {
      const saved = await flushAndSave();
      if (!saved) { setRunStatus?.([nodeId], "idle"); setRunBusy(false); return; }
      // ADR-0128 — async run: POST → poll runs/:runId; the render model is read
      // from the node cache (`render_out`) after the run, not from the receipt.
      await runController.startRun({
        operationId,
        nodeId,
        forceRefresh: false,
        onComplete: async (_run, nodes) => {
          await loadRenderModel();
          const target = nodes.find((n) => n.node_id === nodeId) ?? null;
          const runAt = target?.finished_at ?? new Date().toISOString();
          const rowCount = target?.row_count ?? null;
          setLastRunAt(runAt);
          setLastRowCount(rowCount);
          sessionLastRunByNodeId.set(nodeId, { lastRunAt: runAt, lastRowCount: rowCount });
          setRunStatus?.([nodeId], "success");
          // Refresh upstream columns too — first run populates the cache.
          void loadUpstreamColumns();
        },
        onError: (error) => {
          setRunError(error ?? "Run failed.");
          setRunStatus?.([nodeId], "error");
        },
        onCancelled: () => { setRunStatus?.([nodeId], "idle"); },
        onFailure: (err) => {
          setRunError(err instanceof Error ? err.message : String(err));
          setRunStatus?.([nodeId], "error");
        },
      });
    } finally {
      setRunBusy(false);
    }
  }, [operationId, nodeId, flushAndSave, loadUpstreamColumns, setRunStatus, runController, loadRenderModel]);

  // ─── spec mutations ───
  const writeSpec = useCallback((next: KpiCardVizSpecV1) => {
    setNodeData(nodeId, (d) => ({
      ...d,
      config: { ...d.config, spec_kpi_card: next },
    }));
  }, [nodeId, setNodeData]);

  const addItem = useCallback(() => {
    const firstNumeric = upstreamColumns.find((c) => NUMERIC_TYPES.has(c.type.toLowerCase()));
    const firstAny = upstreamColumns[0];
    const fallback = firstNumeric ?? firstAny;
    const next: KpiCardVizSpecV1 = {
      ...currentSpec,
      items: [
        ...currentSpec.items,
        { field: fallback?.name ?? "", aggregation: firstNumeric ? "sum" : "count" },
      ],
    };
    writeSpec(next);
  }, [currentSpec, upstreamColumns, writeSpec]);

  const updateItem = useCallback((idx: number, patch: Partial<KpiCardItem>) => {
    const items = currentSpec.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    writeSpec({ ...currentSpec, items });
  }, [currentSpec, writeSpec]);

  const removeItem = useCallback((idx: number) => {
    const items = currentSpec.items.filter((_, i) => i !== idx);
    writeSpec({ ...currentSpec, items });
  }, [currentSpec, writeSpec]);

  // ─── AI chat ───
  const {
    sessionId,
    setSessionId,
    messages,
    setMessages,
    replyPending,
    error: chatThreadError,
    resetChat,
    cancelChat,
  } = useAiChatThread({
    threadKey: `${operationId}/${nodeId}/kpi`,
    loadLatest: useCallback(async () => {
      const res = await apiFetch<{
        session: { id: string; created_at?: string };
        messages: Array<{ id: string; role: string; content: string; created_at?: string }>;
      }>(`/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/viz/sessions/latest`);
      return {
        sessionId: res.session.id,
        messages: res.messages.map((m) => ({
          id: m.id,
          role: m.role as AiChatMessage["role"],
          content: m.content,
          created_at: m.created_at ?? "",
        })),
      };
    }, [operationId, nodeId]),
    createNewSession: useCallback(async () => {
      const res = await apiFetch<{ session: { id: string } }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/viz/sessions`,
        { method: "POST", body: {} }
      );
      return { sessionId: res.session.id };
    }, [operationId, nodeId]),
    cancelChat: useCallback(async (sid: string) => {
      await apiFetch(
        `/api/tenant/ai/sessions/${encodeURIComponent(sid)}/abort`,
        { method: "POST", body: {} }
      );
    }, []),
  });
  const [draftMessage, setDraftMessage] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  // ADR-0207 §3.4: the hub's error code, for the admin's link under an AI-route refusal.
  const [chatErrorCode, setChatErrorCode] = useState<string | null>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [messages]);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;
    const res = await apiFetch<{ session: { id: string } }>(
      `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/viz/sessions`,
      { method: "POST", body: {} }
    );
    setSessionId(res.session.id);
    return res.session.id;
  }, [sessionId, operationId, nodeId, setSessionId]);

  const sendChat = useCallback(async () => {
    const text = draftMessage.trim();
    if (!text) return;
    setChatBusy(true);
    setChatError(null); setChatErrorCode(null);
    const optimisticId = `local-${Date.now()}`;
    setMessages((prev) => [...prev, { id: optimisticId, role: "user", content: text, created_at: new Date().toISOString() }]);
    setDraftMessage("");
    try {
      const sid = await ensureSession();
      // Save the spec first so the orchestrator reads the latest currentSpec.
      const saved = await flushAndSave();
      if (!saved) { setChatError("Failed to save before sending"); return; }
      const data = await apiFetch<{
        assistantMessage: { id: string; displayText: string };
        renderModel?: unknown;
        meta?: { persistableKpiSpec?: KpiCardVizSpecV1; responseMode?: string };
      }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/viz/sessions/${encodeURIComponent(sid)}/messages`,
        {
          method: "POST",
          body: {
            message: text,
            interactionMode,
            ...(selectedAiModelId ? { model: selectedAiModelId } : {}),
          },
        }
      );
      setMessages((prev) => [...prev, { id: data.assistantMessage.id, role: "assistant", content: data.assistantMessage.displayText, created_at: new Date().toISOString() }]);
      // Auto-apply the merged spec — no Apply button per spec §3.2b.
      const newSpec = data.meta?.persistableKpiSpec;
      if (newSpec) {
        const v = validateKpiCardSpec(newSpec);
        if (v.ok) {
          writeSpec(v.spec);
          await flushAndSave();
          toast.push({ kind: "success", message: `KPI updated — ${v.spec.items.length} item${v.spec.items.length === 1 ? "" : "s"}` });
        }
      }
      // Refresh the rendered preview from the dispatcher's render_out.
      const rm = data.renderModel as Record<string, unknown> | undefined;
      if (rm && rm.kind === "kpi" && Array.isArray(rm.metrics)) {
        setRenderModel(rm as unknown as RenderModelKpi);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setChatError(msg); setChatErrorCode(aiRouteErrorCode(e));
      // Remove the optimistic user message on failure so the user can retry.
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setDraftMessage(text);
    } finally {
      setChatBusy(false);
    }
  }, [draftMessage, ensureSession, flushAndSave, interactionMode, operationId, nodeId, selectedAiModelId, writeSpec, toast]);

  // ─── slot content ───
  const infoContent = (
    <div className="card" style={{ display: "grid", gap: 10 }}>
      <div className="field">
        <label className="label">Node Name</label>
        <input
          className="input"
          type="text"
          value={selectedNode.data.label}
          onChange={(e) => setNodeData(nodeId, (d) => ({ ...d, label: e.target.value }))}
          onBlur={() => void flushAndSave()}
          placeholder="Unnamed"
        />
      </div>
    </div>
  );

  const setupContent = (
    <div className="card" style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontWeight: 600 }}>Items ({currentSpec.items.length})</div>
        <button className="btn btnSm" type="button" onClick={addItem} disabled={upstreamColumns.length === 0}>
          + Add item
        </button>
      </div>
      {upstreamLoading ? <div className="muted" style={{ fontSize: 12 }}>Loading upstream columns…</div> : null}
      {upstreamError ? <div className="help" style={{ color: "var(--danger-text)" }}>{upstreamError}</div> : null}
      {!upstreamLoading && upstreamColumns.length === 0 ? (
        <div className="muted" style={{ fontSize: 12 }}>No upstream schema cached yet — click Run once to populate column choices.</div>
      ) : null}
      {currentSpec.items.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, fontStyle: "italic" }}>No items yet. Add one or describe what you want in the AI tab.</div>
      ) : null}
      {currentSpec.items.map((item, idx) => {
        const eligible = item.aggregation === "count"
          ? upstreamColumns
          : upstreamColumns.filter((c) => NUMERIC_TYPES.has(c.type.toLowerCase()));
        const fieldInEligible = eligible.some((c) => c.name === item.field);
        return (
          <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 32px", gap: 6, alignItems: "end" }}>
            <div className="field">
              <span className="label">Field</span>
              <select
                className="select"
                value={item.field}
                onChange={(e) => updateItem(idx, { field: e.target.value })}
                onBlur={() => void flushAndSave()}
              >
                {item.field && !fieldInEligible ? (
                  <option value={item.field}>{item.field} (not in schema)</option>
                ) : null}
                {eligible.map((c) => (
                  <option key={c.name} value={c.name}>{c.name} ({c.type})</option>
                ))}
              </select>
            </div>
            <div className="field">
              <span className="label">Aggregation</span>
              <select
                className="select"
                value={item.aggregation}
                onChange={(e) => updateItem(idx, { aggregation: e.target.value as KpiCardItem["aggregation"] })}
                onBlur={() => void flushAndSave()}
              >
                {KPI_AGGREGATIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="field">
              <span className="label">Label</span>
              <input
                className="input"
                type="text"
                value={item.label ?? ""}
                onChange={(e) => updateItem(idx, { label: e.target.value })}
                onBlur={() => void flushAndSave()}
                placeholder="(auto)"
              />
            </div>
            <button className="btn btnSm btnDanger" type="button" onClick={() => { removeItem(idx); void flushAndSave(); }} aria-label={`Remove item ${idx + 1}`}>
              ×
            </button>
            <div style={{ gridColumn: "1 / span 4", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, paddingLeft: 4 }}>
              <div className="field">
                <span className="label">Format</span>
                <select
                  className="select"
                  value={item.format?.kind ?? "none"}
                  onChange={(e) => {
                    const kind = e.target.value as "none" | "number" | "currency" | "percent";
                    const next = kind === "none" ? undefined : { kind, ...(kind === "currency" ? { currency: item.format?.currency ?? "USD" } : {}), ...(item.format?.decimals != null ? { decimals: item.format.decimals } : {}) };
                    updateItem(idx, { format: next });
                  }}
                  onBlur={() => void flushAndSave()}
                >
                  {KPI_FORMAT_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <div className="field">
                <span className="label">Decimals</span>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={6}
                  value={item.format?.decimals ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const v = raw === "" ? undefined : Math.max(0, Math.min(6, Math.floor(Number(raw))));
                    updateItem(idx, { format: { ...(item.format ?? { kind: "number" }), decimals: v } as KpiCardItem["format"] });
                  }}
                  onBlur={() => void flushAndSave()}
                  placeholder="auto"
                />
              </div>
              {item.format?.kind === "currency" ? (
                <div className="field">
                  <span className="label">Currency (ISO)</span>
                  <input
                    className="input"
                    type="text"
                    maxLength={3}
                    value={item.format?.currency ?? "USD"}
                    onChange={(e) => updateItem(idx, { format: { ...(item.format ?? { kind: "currency" }), kind: "currency", currency: e.target.value.toUpperCase() } })}
                    onBlur={() => void flushAndSave()}
                    placeholder="USD"
                  />
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );

  const dataContent = (
    <div className="card">
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Preview</div>
      {renderError ? <div className="help" style={{ color: "var(--danger-text)" }}>{renderError}</div> : null}
      {renderLoading ? <div className="muted">Loading…</div> : null}
      {!renderLoading && !renderModel ? (
        <div className="muted" style={{ fontSize: 13, fontStyle: "italic" }}>No preview yet — click Run to render the KPI cards.</div>
      ) : null}
      {renderModel ? <KpiCard metrics={renderModel.metrics} style={renderModel.style} /> : null}
    </div>
  );

  const aiContent = (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 8 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", flexShrink: 0 }}>
        <div style={{ fontWeight: 600 }}>Atlas KPI agent</div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btnSm"
          onClick={() =>
            void (async () => {
              const ok = await confirm.confirm({
                title: "Reset this chat?",
                detail:
                  "The current conversation is cleared from view and a fresh session starts. Past messages stay on the server but are no longer visible here.",
                confirmLabel: "Reset chat",
              });
              if (!ok) return;
              await resetChat();
            })()
          }
          disabled={messages.length === 0}
          title="Start a new conversation. Past messages stay on the server but are no longer visible here."
        >
          Reset chat
        </button>
        <select
          className="select"
          style={{ width: 110 }}
          value={interactionMode}
          onChange={(e) => setInteractionMode(e.target.value as InteractionMode)}
          title="Agent mode auto-applies a new spec; Ask mode answers questions only"
        >
          <option value="agent">Agent</option>
          <option value="ask">Ask</option>
        </select>
        {tenantAiModels.length > 0 && selectedAiModelId ? (
          <select
            className="select"
            style={{ width: 200 }}
            value={selectedAiModelId}
            onChange={(e) => onTenantAiModelChange?.(e.target.value)}
            disabled={!onTenantAiModelChange}
            title="Model used for the next AI message"
          >
            {tenantAiModels.map((m) => (
              <option key={m.model_id} value={m.model_id}>
                {m.model_id}
                {m.status === "deprecated" ? " (deprecated)" : ""}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <div ref={chatScrollRef} style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 4 }}>
        <ChatMessageList
          messages={messages}
          assistantLabel="Atlas KPI agent"
          emptyState={
            <div className="muted" style={{ fontSize: 13, fontStyle: "italic" }}>
              Tell the agent what KPIs you want — e.g. "Total revenue and average order value, with currency formatting."
            </div>
          }
          trailingNode={<ChatThinkingIndicator visible={replyPending} />}
        />
      </div>
      <ChatErrorBanner message={chatError ?? chatThreadError} code={chatError ? chatErrorCode : null} />
      <ChatComposer
        draft={draftMessage}
        setDraft={setDraftMessage}
        onSend={() => void sendChat()}
        onCancel={() => void cancelChat()}
        inFlight={chatBusy || replyPending}
        placeholder="Describe the KPI cards you want… (Enter to send, Shift+Enter for newline)"
        minRows={3}
        maxRows={12}
      />
    </div>
  );

  // ─── render ───
  return (
    <div style={{ ...INSPECTOR_WIDTH_STYLE, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Persistent action bar — node-global, above the tab rack. */}
      <div style={{ display: "flex", gap: 4, alignItems: "center", borderBottom: "1px solid var(--line)", paddingBottom: 6, marginBottom: 8, flexWrap: "wrap", flexShrink: 0 }}>
        <div className="muted" style={{ fontSize: 12, marginRight: 8 }}>
          {runBusy ? "Running…" : lastRunAt ? (
            <>Last run: {new Date(lastRunAt).toLocaleString()}{typeof lastRowCount === "number" ? ` · ${lastRowCount.toLocaleString()} rows` : ""}</>
          ) : "Not yet executed"}
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn btnPrimary btnSm" type="button" onClick={() => void runNode()} disabled={runBusy}>
          {runBusy ? "Running…" : "▶ Run"}
        </button>
      </div>

      {runError ? <div className="help" style={{ color: "var(--danger-text)", flexShrink: 0, marginBottom: 8 }}>{runError}</div> : null}

      <InspectorTabs
        surfaceId="operationEditor"
        tabGroupId="kpi_card"
        defaultSlot="setup"
        tabs={[
          { slot: "info", content: infoContent },
          { slot: "setup", content: setupContent },
          { slot: "data", content: dataContent },
          { slot: "ai", fill: true, content: aiContent },
        ]}
      />
    </div>
  );
}
